import React, { useState } from "react";
import { useDevices } from "../hooks/useDevices";
import { DeviceSummary } from "../types";
import { api } from "../services/api";
import {
  ServerStats,
  BandwidthUsage,
  ClientConnections,
  SystemAlerts,
} from "./DashboardWidgets";
import { DeviceTable } from "./DeviceTable";
import { EnrollTokenModal } from "./EnrollTokenModal";
import { DeviceDetailModal } from "./DeviceDetailModal";

interface DashboardContentProps {
  selectedTenantId?: string;
}

export const DashboardContent: React.FC<DashboardContentProps> = ({
  selectedTenantId,
}) => {
  const { data, isLoading, isError, error, refetch } = useDevices(selectedTenantId);
  const devices = data || [];
  const [selectedDevice, setSelectedDevice] = useState<DeviceSummary | null>(null);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState<boolean>(false);
  const [keyInput, setKeyInput] = useState<string>(api.getApiKey());
  const unauthorized = isError && String(error?.message || "").includes("unauthorized");

  const handleSaveKey = (e: React.FormEvent) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    api.setApiKey(k);
    refetch();
  };

  // Stats calculations
  const totalCount = devices.length;
  const activeCount = devices.filter((d) => d.status === "active" && !d.kill_switched).length;
  const pendingCount = devices.filter((d) => d.status === "pending").length;
  const killedCount = devices.filter((d) => d.kill_switched).length;

  const totalRxBytes = devices.reduce((sum, d) => sum + (d.rx_bytes || 0), 0);
  const totalTxBytes = devices.reduce((sum, d) => sum + (d.tx_bytes || 0), 0);
  const totalMB = ((totalRxBytes + totalTxBytes) / (1024 * 1024)).toFixed(1);

  // Dynamic alerts
  const alerts: string[] = [];
  if (pendingCount > 0) {
    alerts.push(`${pendingCount} router(s) awaiting token enrollment activation`);
  }
  if (killedCount > 0) {
    alerts.push(`${killedCount} device(s) currently kill-switched by admin`);
  }
  if (devices.length === 0) {
    alerts.push("No routers enrolled yet. Click 'Enroll Router' to generate a token.");
  } else {
    alerts.push("Control Plane operating normally (0 network errors)");
  }

  // Activity feed
  const recentActivity = devices.length > 0
    ? devices.slice(0, 4).map((d) => {
        const timeAgo = d.last_seen ? new Date(d.last_seen).toLocaleTimeString() : "just now";
        return `${d.name} (${d.mesh_ip}) - ${d.status.toUpperCase()} at ${timeAgo}`;
      })
    : ["No recent router heartbeat activity recorded"];

  return (
    <main className="dashboard-content">
      {isLoading && <div className="loading-bar">Syncing MeshRouter fleet data...</div>}
      
      {isError && (
        <div className="error-card card margin-bottom">
          <h3>Connection Warning</h3>
          <p>Unable to fetch fleet devices: {error?.message}</p>

          {unauthorized && (
            <form onSubmit={handleSaveKey} className="form-group inline-group margin-top">
              <label htmlFor="inline-api-key">Control Plane API Key:</label>
              <input
                id="inline-api-key"
                name="inline-api-key"
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Paste X-API-Key and save..."
                required
              />
              <button type="submit" className="btn btn-primary btn-sm">
                Save Key
              </button>
            </form>
          )}

          <button className="btn btn-sm btn-outline margin-top" onClick={() => refetch()}>
            Retry Connection
          </button>
        </div>
      )}

      <div className="widgets-grid margin-bottom">
        <ServerStats
          title="Fleet Health & Active Routers"
          metric1={`${activeCount} / ${totalCount}`}
          metric2="Active Mesh Nodes"
          metric3={`${pendingCount} Pending Enrollment`}
          trend={killedCount > 0 ? `${killedCount} Killed` : "100% Healthy"}
          trendColor={killedCount > 0 ? "text-danger" : "text-success"}
        />

        <BandwidthUsage
          title="Control Plane Mesh Traffic"
          usagePercentage={totalCount > 0 ? Math.min(100, Math.round((activeCount / Math.max(1, totalCount)) * 100)) : 0}
          downloadSpeed={`${(totalRxBytes / (1024 * 1024)).toFixed(2)} MB RX`}
          uploadSpeed={`${(totalTxBytes / (1024 * 1024)).toFixed(2)} MB TX`}
        />

        <ClientConnections
          title="Registered Devices"
          connectedClients={activeCount}
          totalClients={totalCount}
          recentActivity={recentActivity}
        />

        <SystemAlerts
          title="System & Security Alerts"
          alerts={alerts}
        />
      </div>

      <DeviceTable
        devices={devices}
        onSelectDevice={(dev) => setSelectedDevice(dev)}
        onOpenEnrollModal={() => setIsEnrollModalOpen(true)}
      />

      <EnrollTokenModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
      />

      <DeviceDetailModal
        device={selectedDevice}
        onClose={() => setSelectedDevice(null)}
      />
    </main>
  );
};

export default DashboardContent;